import { useEffect, useId, useRef, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { Drawer } from "./Drawer"
import { useBreakpoint } from "../hooks/useMediaQuery"
import { useDetailPane } from "../AppShell"

type SidePaneProps = {
  isOpen: boolean
  onClose?: () => void
  title?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  paneKey?: string
}

export function SidePane({ isOpen, onClose, title, actions, children, paneKey }: SidePaneProps) {
  const isDesktop = useBreakpoint("md")
  const { open, release } = useDetailPane()
  const generatedId = useId()
  const ownerId = paneKey ?? generatedId

  // Track previous open state to detect when closing
  const prevOpenRef = useRef(isOpen)
  const activeOwnerIdRef = useRef(ownerId)
  
  useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = isOpen
    
    // If pane was open and is now closed, release it
    if (wasOpen && !isOpen && isDesktop) {
      release(activeOwnerIdRef.current)
      return
    }
    
    // If not on desktop or not open, don't register
    if (!isDesktop || !isOpen) {
      return
    }

    // Register the desktop shell. The live content is rendered through the
    // portal below so async data and form state cannot become stale.
    activeOwnerIdRef.current = ownerId
    open(ownerId, {
      title,
      actions: undefined,
      content: undefined,
      onClose: null,
    })

    // No cleanup on unmount - we don't want to auto-release when this component unmounts
    // during navigation between supported routes. The release happens when isOpen changes
    // to false or when navigating to unsupported routes (AppShell's auto-close).
  }, [isDesktop, isOpen, ownerId, open, release, title])

  if (!isDesktop) {
    return (
      <Drawer open={isOpen} onClose={onClose} title={title} side="bottom">
        <div className="sidepane-mobile">
          {actions ? <div className="sidepane-mobile__actions">{actions}</div> : null}
          <div className="sidepane-mobile__body">{children}</div>
          {onClose ? (
            <button type="button" className="sidepane-mobile__dismiss" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </Drawer>
    )
  }

  const portalTarget = typeof document === "undefined"
    ? null
    : document.getElementById("app-shell-detail-pane-portal")

  if (!isOpen || !portalTarget) {
    return null
  }

  return createPortal(
    <>
      <header className="sidepane-desktop__header">
        {title ? <div className="sidepane-desktop__title">{title}</div> : null}
        {onClose ? (
          <button type="button" onClick={onClose} className="sidepane-desktop__close">
            Close
          </button>
        ) : null}
      </header>
      {actions ? <div className="sidepane-desktop__actions">{actions}</div> : null}
      <div className="sidepane-desktop__body">{children}</div>
    </>,
    portalTarget,
  )
}

export default SidePane
